// ============================================================
// Toast — transient notification with provider + hook.
//
// Wrap your tree in <ToastProvider /> once, then call useToast()
// from any descendant to push toasts. Toasts auto-dismiss after
// `duration` ms (default 4000).
// ============================================================

import { createContext, useContext, useState, useCallback, useEffect } from 'react'

const TOAST_TINTS = {
  info:     { color: 'var(--olv-cyan)',  border: 'rgba(0,212,255,0.30)'  },
  success:  { color: 'var(--olv-green)', border: 'rgba(0,230,138,0.30)'  },
  warning:  { color: 'var(--olv-amber)', border: 'rgba(255,178,36,0.30)' },
  critical: { color: 'var(--olv-red)',   border: 'rgba(255,59,92,0.32)'  },
}

const ToastContext = createContext(null)

let toastSeq = 0

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const push = useCallback((toast) => {
    const id = ++toastSeq
    setToasts((prev) => [...prev, { id, severity: 'info', duration: 4000, ...toast }])
    return id
  }, [])

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      {children}
      <div
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 400,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastItem({ toast, onDismiss }) {
  useEffect(() => {
    if (!toast.duration) return
    const timer = setTimeout(onDismiss, toast.duration)
    return () => clearTimeout(timer)
  }, [toast.duration, onDismiss])

  const tint = TOAST_TINTS[toast.severity] || TOAST_TINTS.info

  return (
    <div
      role="status"
      style={{
        background: 'var(--olv-elevated)',
        border: `1px solid ${tint.border}`,
        borderRadius: 'var(--olv-radius-card)',
        padding: '10px 14px',
        minWidth: 220,
        maxWidth: 360,
        boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        fontFamily: 'var(--font-body)',
        fontSize: 12,
        color: 'var(--olv-text-primary)',
        pointerEvents: 'auto',
        animation: 'olv-fade-in 240ms var(--ease-standard)',
      }}
    >
      {toast.title && (
        <div
          style={{
            fontSize: 9,
            color: tint.color,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            fontFamily: 'var(--font-mono)',
            marginBottom: 4,
          }}
        >
          {toast.title}
        </div>
      )}
      <div>{toast.body || toast.message}</div>
    </div>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Honest no-op fallback so callers don't need to assert provider presence.
    return { push: () => 0, dismiss: () => {} }
  }
  return ctx
}

// Default export for compatibility with `import Toast from`.
const Toast = { Provider: ToastProvider, useToast }
export default Toast
